import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { getItem, setItem } from '@alltrails/core/utils/localStorage';
import logError from '@alltrails/core/utils/logError';
import { updateHiddenPOIs } from '../redux/reducer';
import { hidePOI } from '../requests/hidePOI';

const hiddenPOIsKey = 'hiddenPOIs';

type UseHidePOIParams = {
  ID: number;
  hiddenPOIs?: number[];
};

const useHidePOI = ({ ID, hiddenPOIs }: UseHidePOIParams) => {
  const dispatch = useDispatch();
  const [isHiding, setIsHiding] = useState(false);
  const [showHideConfirmation, setShowHideConfirmation] = useState(false);

  useEffect(() => {
    const persistedHiddenPOIs = getItem<number[]>(hiddenPOIsKey);
    if (persistedHiddenPOIs) {
      dispatch(updateHiddenPOIs(persistedHiddenPOIs));
    }
  }, [dispatch]);

  const onHidePOI = useCallback(
    async (e?: React.MouseEvent<HTMLElement>) => {
      e?.preventDefault();

      setIsHiding(true);
      try {
        const response = await hidePOI(ID);
        if (response.status === 'ok') {
          const hidden = [...new Set([...(hiddenPOIs || []), ID])];
          dispatch(updateHiddenPOIs(hidden));
          setItem(hiddenPOIsKey, hidden);
        }
        setIsHiding(false);
        setShowHideConfirmation(false);
      } catch (error) {
        logError(new Error('Error when hiding the POI', { cause: error }));
        setIsHiding(false);
      }
    },
    [dispatch, hiddenPOIs, ID]
  );

  return {
    isHiding,
    showHideConfirmation,
    setShowHideConfirmation,
    onHidePOI
  };
};

export default useHidePOI;
